import {useCallback} from 'react';

import Auth, {CognitoUser} from '@aws-amplify/auth'
import {useAppContext} from '@/context/app';
import {useNavigate} from 'react-router-dom';
import {IAuthUserInfo, IAppUserUpdateUp} from '@/types/Auth.ts';
import useUsersApi from '@/hooks/user/useUsersApi.ts';
import useTicketStore from '@/components/TicketsModule/stores/ticket-store-map';


interface IUseAuthHook {
    user: IAuthUserInfo | null
    login: (email: string, password: string) => Promise<void>
    forgot: (email: string) => Promise<void>
    resendCode: (email: string) => Promise<void>
    completeNewPassword: (user: unknown, password: string) => Promise<void>
    setUserAttributes: (params: Pick<IAppUserUpdateUp, 'name'>) => Promise<void>
    forgotSubmit: (code: string, password: string, email?: string) => Promise<void>
    changePassword: (oldPassword: string, newPassword: string) => Promise<void>
    logOut: () => Promise<void>
    checkAuth: () => Promise<void>
    loadRoles: () => Promise<void>
    needRefreshAuthTokens: () => boolean
}

const useAuth = (): IUseAuthHook => {
    const navigate = useNavigate()
    const {auth, setAuthUserInfo, cleanState} = useAppContext()
    const {updateMe} = useUsersApi()


    const token = auth?.token
    const login = (email: string, password: string): Promise<void> => {

        return new Promise((resolve, reject) => {
            Auth.signIn(email, password).then((value) => {
                const user = value as CognitoUser
                const session = user.getSignInUserSession()

                if (!session && value.challengeName === 'NEW_PASSWORD_REQUIRED') {
                    setAuthUserInfo({
                        user: null,
                        roles: [],
                        permissions: [],
                        tempUser: user,
                        tempPassword: password,
                    });
                    reject({code: 'PasswordResetRequiredException'})
                } else if (session) {
                    setAuthUserInfo({
                        token: session.getIdToken().getJwtToken(),
                        email: session.getIdToken().decodePayload()['email'] as string,
                        user: null,
                        roles: [],
                        permissions: [],
                        tempPassword: password,
                        expireAt: session.getIdToken().getExpiration()
                    })
                    //Activar la hidratacion de la store de tickets
                    useTicketStore.persist.rehydrate();
                    resolve()
                } else {
                    reject('No session Associated!!')
                }
                resolve()
            }).catch((err) => {
                cleanState()
                reject(err)
            })
        })
    }


    const resendCode = (email: string): Promise<void> => {
        return new Promise((resolve, reject) => {

            Auth.resendSignUp(email).then(() => {
                resolve();
            }).catch((err) => {
                reject(err)
            })
        })
    }

    const logOut = (): Promise<void> => {
        return new Promise((resolve, reject) => {
            Promise.all([
                Auth.signOut({global: true}),
                updateMe({state: 'disconnected'}),
                Promise.resolve().finally(() => navigate('/auth/login'))
            ]).then(() => {
                resolve()
            }).catch(err => {
                reject(err)
            }).finally(() => {
                cleanState()
            })
        })
    }

    const forgot = (email: string): Promise<void> => {
        return new Promise((resolve, reject) => {
            Auth.forgotPassword(email).then(() => {
                // setAuthTempInfo({email})
                resolve()
            }).catch((err) => {
                reject(err)
            })
        })
    }

    const completeNewPassword = (user: unknown, password: string): Promise<void> => {
        return new Promise((resolve, reject) => {
            Auth.completeNewPassword(user, password).then(() => {
                resolve()
            }).catch((err) => {
                reject(err)
            })
        })
    }

    const forgotSubmit = (code: string, password: string, email?: string): Promise<void> => {
        return new Promise((resolve, reject) => {
            //email = email ? email : auth?.tempInfo?.email
            Auth.forgotPasswordSubmit(email as string, code, password).then(() => {
                cleanState()
                resolve()
            }).catch((err) => {
                reject(err)
            })
        })
    }

    const setUserAttributes = (params: Pick<IAppUserUpdateUp, 'name'>): Promise<void> => {
        return new Promise((resolve, reject) => {
            Auth.currentAuthenticatedUser().then(async (user) => {
                try {
                    await Auth.updateUserAttributes(user, params);
                    resolve()
                } catch (err) {
                    reject(err)
                }
            }).catch((err) => reject(err))
        })
    }


    const changePassword = (oldPassword: string, newPassword: string): Promise<void> => {
        return new Promise((resolve, reject) => {
            Auth.currentAuthenticatedUser().then(async (user) => {
                try {
                    await Auth.changePassword(user, oldPassword, newPassword);
                    resolve()
                } catch (err) {
                    reject(err)
                }
            }).catch((err) => reject(err))
        })
    }


    const checkAuth = useCallback((): Promise<void> => {
        // eslint-disable-next-line no-async-promise-executor
        return new Promise(async (resolve, reject) => {
            try {
                const session = await Auth.currentSession();
                const idToken = session.getIdToken();

                setAuthUserInfo({
                    ...auth,
                    token: idToken.getJwtToken(),
                    email: idToken.decodePayload()['email'] as string,
                    expireAt: idToken.getExpiration()
                });
                resolve()

            } catch (err) {
                cleanState();
                logOut().finally();
                reject(err)
            }

        })

    }, [auth]);


    const loadRoles = useCallback((): Promise<void> => {
        return new Promise((resolve, reject) => {
            Auth.currentUserInfo().then(async (info) => {
                if (info) {
                    try {
                        // const rolesAttr = Array.from<string>(JSON.parse(info?.attributes["custom:roles"] ?? "[]"))
                        //
                        // if (rolesAttr.length > 0) {
                        //     const roles = await apiGet<Array<IAdminRole>>(`/admin/roles/ids/${rolesAttr.join(",")}`);
                        //     setUserRoles(roles)
                        // }
                        resolve()
                    } catch (err) {
                        reject(err)
                    }
                } else {
                    reject('No user info Associated!!')
                }
            }).catch((err) => {
                cleanState()
                reject(err)
            })
        })
    }, [token])

    const needRefreshAuthTokens = useCallback((): boolean => {
        const expireAt = auth.expireAt;
        const nowInSeconds = Math.floor(Date.now() / 1000);
        const thresholdSeconds = 10 * 60;
        
        if(!expireAt) return false;

        return Boolean((expireAt - nowInSeconds) <= thresholdSeconds);
    }, [auth]);

    return {
        user: auth,
        login,
        logOut,
        forgot,
        resendCode,
        forgotSubmit,
        completeNewPassword,
        setUserAttributes,
        changePassword,
        checkAuth,
        loadRoles,
        needRefreshAuthTokens
    }
}

export default useAuth